<script setup lang="ts">
/**
 * Chip — small pill for filters, tags, status markers.
 *
 * Variants from prototype (231 occurrences):
 *   - default: neutral surface
 *   - active:  selected/current filter
 *   - ai:      AI-tagged chip (violet tint)
 *   - live:    pulsing indicator for live data
 *
 * Optional `count` displays a number after the label
 * (the prototype's "All · 428,712" pattern).
 */
import { computed } from 'vue'

interface Props {
  variant?: 'default' | 'active' | 'ai' | 'live'
  count?: number | string
  clickable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  clickable: false,
})

defineEmits<{
  click: [event: MouseEvent]
}>()

const classes = computed(() => {
  const base =
    'inline-flex items-center gap-1.5 h-6 px-2 text-[11px] font-medium ' +
    'rounded-sm border transition-colors'

  const variantCls = {
    default: 'bg-surface-2 text-text-3 border-border',
    active:
      'bg-brand-soft text-brand-deep border-brand-line shadow-glow',
    ai:
      'bg-violet-soft text-violet border-violet-line',
    live:
      'bg-green-soft text-green border-green-line',
  }[props.variant]

  const interactive = props.clickable
    ? 'cursor-pointer hover:bg-surface-3 hover:text-text-1'
    : ''

  return `${base} ${variantCls} ${interactive}`
})
</script>

<template>
  <span
    :class="classes"
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    @click="clickable && $emit('click', $event)"
    @keydown.enter="clickable && $emit('click', $event as unknown as MouseEvent)"
  >
    <span
      v-if="variant === 'live'"
      class="w-1.5 h-1.5 rounded-full bg-green animate-pulse"
      aria-hidden="true"
    />
    <slot />
    <span
      v-if="count !== undefined"
      class="text-text-4 font-mono ml-0.5"
    >· {{ count }}</span>
  </span>
</template>
